import { defineStore } from "pinia";

/**
 * Config store — runtime configuration (API base URL, admin password).
 *
 * In dev, the Vite proxy serves `/api` → `http://localhost:8787`, so the
 * default base URL is empty (same-origin). The admin password is read from
 * localStorage (set by the Login page) and attached as `X-Admin-Password`
 * by `apiFetch`.
 */

const ADMIN_PASSWORD_KEY = "kff-admin-password";

function readPassword(): string {
  try {
    return localStorage.getItem(ADMIN_PASSWORD_KEY) ?? "";
  } catch {
    return "";
  }
}

function writePassword(value: string): void {
  try {
    if (value === "") {
      localStorage.removeItem(ADMIN_PASSWORD_KEY);
    } else {
      localStorage.setItem(ADMIN_PASSWORD_KEY, value);
    }
  } catch {
    // ignore
  }
}

interface ConfigState {
  apiBaseUrl: string;
  adminPassword: string;
}

export const useConfigStore = defineStore("config", {
  state: (): ConfigState => ({
    // Empty = same-origin. Vite proxy handles `/api` in dev.
    apiBaseUrl: (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "",
    adminPassword: "",
  }),

  actions: {
    bootstrap(): void {
      this.adminPassword = readPassword();
    },

    setApiBaseUrl(url: string): void {
      this.apiBaseUrl = url;
    },

    setAdminPassword(password: string): void {
      this.adminPassword = password;
      writePassword(password);
    },

    clearAdminPassword(): void {
      this.adminPassword = "";
      writePassword("");
    },
  },
});